import React from 'react';

const CARD_IMG = '/Images/News/deepseek-v4-card.png';

const DeepSeekV4 = () => (
  <>
    <figure className="news-article-figure news-article-figure--hero">
      <img
        src={CARD_IMG}
        alt="DeepSeek wordmark on a dark card background (editorial image for V4 preview)."
        loading="lazy"
        decoding="async"
      />
      <figcaption>
        DeepSeek V4 preview: open mixture-of-expert models with a one-million-token context. This
        card uses the public DeepSeek wordmark on a simple dark background.
      </figcaption>
    </figure>

    <article className="news-article-body">
      <p className="news-lead">
        On April 24, 2026, DeepSeek announced <strong>DeepSeek-V4 Preview</strong>: a pair of
        open-weight mixture-of-experts (MoE) language models built around an efficient
        one-million-token context window, with API access and integration into DeepSeek&apos;s
        own chat product the same day.
      </p>

      <h2>Two model lines: Pro and Flash</h2>
      <p>
        The release spans two tiers. <strong>DeepSeek-V4-Pro</strong> is the larger variant:
        about <strong>1.6 trillion</strong> total parameters with roughly{' '}
        <strong>49 billion active</strong> per token. <strong>DeepSeek-V4-Flash</strong> is the
        faster, smaller MoE: about <strong>284 billion</strong> total parameters with{' '}
        <strong>13 billion active</strong> per token. DeepSeek positions Pro for peak quality
        and Flash for latency and API cost.
      </p>

      <h2>1M context as the default</h2>
      <p>
        A headline of the launch is operational: <strong>one million tokens</strong> of context
        is described as the standard across DeepSeek&apos;s official services for these models,
        not a special tier. That matters for long documents, codebases, and agent loops that
        accumulate state. If the efficiency claims hold in production, it eases the usual tension
        between &quot;long context&quot; and &quot;unaffordable memory.&quot;
      </p>

      <h2>Architecture: attention and efficiency</h2>
      <p>
        DeepSeek highlights a combination of <strong>token-wise compression</strong> and{' '}
        <strong>DSA (DeepSeek Sparse Attention)</strong>, meaning sparse, structured attention
        meant to cut compute and KV-cache footprint on very long sequences. The company frames this
        as the enabler for practical 1M-context inference; the technical write-up is in the
        public PDF linked from their announcement and Hugging Face model cards.
      </p>

      <h2>Reasoning modes and API migration</h2>
      <p>
        Both models support <strong>thinking and non-thinking</strong> modes (see DeepSeek&apos;s
        thinking-mode guide). Developers can call <code>deepseek-v4-pro</code> or{' '}
        <code>deepseek-v4-flash</code> while keeping the same API
        base URL; the API supports both OpenAI-style Chat Completions and Anthropic-style
        interfaces, which reduces friction for existing integrations.
        Users can also try the models in <strong>Expert Mode</strong> (Pro) and{' '}
        <strong>Instant Mode</strong> (Flash) on <strong>chat.deepseek.com</strong>.
      </p>
      <p>
        DeepSeek states that legacy endpoints <code>deepseek-chat</code> and{' '}
        <code>deepseek-reasoner</code> will be{' '}
        <strong>fully retired after July 24, 2026, 15:59 UTC</strong>, with traffic already
        routing to V4-Flash equivalents. Teams on older model names should plan a migration.
      </p>

      <h2>Open weights and agent tooling</h2>
      <p>
        Weights and collection metadata are on Hugging Face under an open release alongside the
        technical report PDF. DeepSeek also emphasises <strong>agentic coding</strong>: they
        report strong scores on public agent-coding benchmarks for open models, and note
        compatibility with agent workflows (including third-party tools they name in their
        announcement). As always, treat benchmark leaderboards as one signal, not the whole
        product story.
      </p>

      <div className="news-insight-box">
        <h3>Why it matters</h3>
        <p>
          After the <strong>DeepSeek R1</strong> moment in 2025, V4 is the next act: pushing{' '}
          <strong>open weights</strong>, <strong>very long context</strong>, and{' '}
          <strong>MoE efficiency</strong> into one coordinated release with immediate API
          and chat access. For enterprises, the practical questions stay familiar: hosting cost at
          1M context, evaluations on your own data, and compliance. The frontier for self-hosted
          and hybrid setups still moves fast.
        </p>
      </div>
    </article>
  </>
);

export default DeepSeekV4;
